import { derivePath } from "ed25519-hd-key";
import { mnemonicToSeedSync } from "bip39";
import {
  ComputeBudgetProgram,
  Connection,
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
  sendAndConfirmTransaction,
} from "@solana/web3.js";
import { encode, decode } from "bs58";

//@ts-ignore
import * as BufferLayout from "buffer-layout";
import {
  createAssociatedTokenAccountInstruction,
  createTransferInstruction,
  getAccount,
  getAssociatedTokenAddress,
  getMint,
  // getOrCreateAssociatedTokenAccount,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  TokenInvalidAccountOwnerError,
  createTransferCheckedWithTransferHookInstruction,
  getTransferHook,
} from "@solana/spl-token";
import {
  FeeResponse,
  SendNftInput,
  SendPayload,
  TxnInstruction,
  TxnResponse,
  CreateAtaIxResponse,
  SolanaArgs,
  Gas,
  ShyftSignResponse,
  ILitCustomAuthSignerSolana,
} from "./types";

import nacl from "tweetnacl";
import naclUtil from "tweetnacl-util";

import {
  customErrorMessage,
  isSignedEncodedTxn,
  isTxnSignature,
  viewInExplorerLink,
} from "./utils";

// import { Network, ShyftSdk } from "@shyft-to/js";
import { ShyftService, errorHandler } from "./services";
import { CHAIN_ERROR } from "@tria-sdk/constants";
import { SOLANA_DATA_VALIDATION, SOLANA_ERROR } from "./utils/errors";

export { Transaction };

export const rpcUrl = {
  mainnet:
    "https://mainnet.helius-rpc.com/?api-key=f844e236-cb09-4994-a3f4-e9d54de9e1e6",
  devnet:
    "https://devnet.helius-rpc.com/?api-key=f844e236-cb09-4994-a3f4-e9d54de9e1e6",
};

export class Solana {
  public connection: Connection;
  devnet?: boolean;
  private rpcUrl: string;
  network: "mainnet-beta" | "devnet";
  chainName: "SOLANA" | "SOLANA-DEVNET";
  isSponsored?: boolean = false;
  isGasAbstracted?: boolean = false;

  private triaFeeDepositAddress?: PublicKey;
  // = new PublicKey(
  //   "DjCsL575RP2A2qvDyav6eQovAPVGzoGdNZvh18nAwLWH"
  // );
  // shyft: ShyftSdk;
  // shyftApiKey: string = "sQ0KAuxnuwXEeqW5";

  shyftService?: ShyftService;
  triaFeePayerAddress: string = "6cQe9dKDiXsSAumxr42y2Gp93LFPDyL9p6kpZ9EL4D9P";
  feePayer?: PublicKey;

  //'https://api.mainnet-beta.solana.com';

  ACCOUNT_LAYOUT = BufferLayout.struct([
    BufferLayout.blob(32, "mint"),
    BufferLayout.blob(32, "owner"),
    BufferLayout.nu64("amount"),
    BufferLayout.blob(93),
  ]);

  constructor({
    chainName,
    devnet,
    // feePayer,
    triaFeeDepositAddress,
    apiUrl,
    accessToken,
    isSponsored,
    isGasAbstracted,
    shyftApiKey,
  }: SolanaArgs = {}) {
    if (devnet || chainName == "SOLANA-DEVNET") {
      this.devnet = true;
      this.rpcUrl = rpcUrl.devnet;
      this.network = "devnet";
      this.chainName = "SOLANA-DEVNET";
    } else {
      this.rpcUrl = rpcUrl.mainnet;
      this.network = "mainnet-beta";
      this.chainName = "SOLANA";
    }
    this.connection = new Connection(this.rpcUrl, {
      commitment: "confirmed",
    });

    this.isSponsored = isSponsored;
    this.isGasAbstracted = isGasAbstracted;

    if (triaFeeDepositAddress) {
      this.triaFeeDepositAddress = new PublicKey(triaFeeDepositAddress);
    }

    if (apiUrl) {
      this.shyftService = new ShyftService({
        apiUrl: apiUrl as string,
        apiKey: shyftApiKey,
        accessToken: accessToken as string,
      });
    }
  }

  async init() {
    if (this.isSponsored || this.isGasAbstracted) {
      console.log("Solana sponsored txn!!");
      if (this.shyftService?.apiKey) {
        const res = await this.shyftService?.getFeePayer();
        if (res?.success) {
          this.feePayer = new PublicKey(res.data.feePayer);
        } else
          throw Error(
            "An error occured in fetching Solana feePayer for the given shyft api key. Please check your shyftApiKey."
          );
      } else {
        this.feePayer = new PublicKey(this.triaFeePayerAddress as string);
      }
    }
  }

  async createWallet(mnemonic: string, derivationPathIndex: number) {
    try {
      // const defaultDerivationPath = `m/44'/501'/0'/0'`;
      const seed = mnemonicToSeedSync(mnemonic);
      const derivedSeed = derivePath(
        `m/44'/501'/${derivationPathIndex}'/0'`,
        seed as unknown as string
      ).key;

      const keyPair = Keypair.fromSeed(derivedSeed);

      return {
        privateKey: encode(keyPair.secretKey), // keyPair.secretKey.toString(),
        address: keyPair.publicKey.toBase58(),
      };
    } catch (err) {
      console.error(`Error in solana createWallet: ${err}`);
      throw err;
    }
  }

  async getBalance(accountAddress: string, tokenAddress?: string) {
    try {
      let balance;
      const publicKey = new PublicKey(accountAddress);
      if (tokenAddress) {
        const tokenPublicKey = new PublicKey(tokenAddress);

        const account = await this.connection.getTokenAccountsByOwner(
          publicKey,
          {
            mint: new PublicKey(tokenAddress),
          }
        );

        const tokenMintAccountInfo = await this.connection.getTokenSupply(
          tokenPublicKey
        );
        const decimals = tokenMintAccountInfo.value?.decimals;
        // console.log('DECIMALS: ', decimals);

        balance =
          account.value.length > 0
            ? this.ACCOUNT_LAYOUT.decode(account.value[0].account.data).amount
            : 0;

        return {
          balanceInUnits: balance,
          decimals,
          balance: balance / 10 ** decimals,
        };
      }

      balance = await this.connection.getBalance(publicKey);
      return {
        balanceInUnits: balance,
        balance: balance / LAMPORTS_PER_SOL,
      };
    } catch (error) {
      console.error(`Error in solana getBalance: ${error}`);
      throw Error(`Error in solana getBalance: ${error}`);
    }
  }

  async getSendFee(
    fromAddress: string,
    recipientAddress: string,
    amount: string,
    tokenAddress?: string
  ): Promise<FeeResponse> {
    try {
      let ataRent = 0;
      let fee = this.feePayer ? 0 : 6000;
      const amountInLamports = Number(amount);

      if (!tokenAddress) {
        const [
          recipientBalance,
          senderBalance,
          minBalForRentRecipient,
          minBalForRentSender,
        ] = await Promise.all([
          this.getBalance(recipientAddress),
          this.getBalance(fromAddress),
          this.getMinBalForRent(recipientAddress),
          this.getMinBalForRent(fromAddress),
        ]);

        if (senderBalance.balanceInUnits < amountInLamports) {
          return {
            success: false,
            message: "INSUFFICIENT_BALANCE_FOR_SENDAMOUNT",
          };
        }

        const { balanceInUnits: senderBalanceInUnits } = senderBalance;

        if (
          senderBalanceInUnits < minBalForRentSender ||
          senderBalanceInUnits - amountInLamports - fee < minBalForRentSender
        ) {
          return {
            success: false,
            message: "INSUFFICIENT_BALANCE_FOR_RENT",
          };
        }

        const { balanceInUnits } = recipientBalance;

        const recipientSolBalancePostTransfer =
          balanceInUnits + amountInLamports;

        if (recipientSolBalancePostTransfer < minBalForRentRecipient) {
          const minAmountNeeded =
            (minBalForRentRecipient - balanceInUnits) / LAMPORTS_PER_SOL;
          return {
            success: false,
            message: "INSUFFICIENT_AMOUNT_FOR_RENT",
            value: minAmountNeeded,
          };
        }
      } else if (tokenAddress) {
        // Determine token program ID first
        let tokenProgramId = TOKEN_PROGRAM_ID;
        const tokenPublicKey = new PublicKey(tokenAddress);
        
        try {
          await getMint(this.connection, tokenPublicKey, undefined, TOKEN_PROGRAM_ID);
          tokenProgramId = TOKEN_PROGRAM_ID;
        } catch (error) {
          if (error instanceof TokenInvalidAccountOwnerError) {
            tokenProgramId = TOKEN_2022_PROGRAM_ID;
          } else {
            throw error;
          }
        }

        const [senderBalance, minBalForRentSender, recipientATAExists] =
          await Promise.all([
            this.getBalance(fromAddress),
            this.getMinBalForRent(fromAddress),
            this.checkRecipientATAExists(
              tokenPublicKey,
              new PublicKey(recipientAddress),
              tokenProgramId
            ),
          ]);

        const senderTokenBalance = await this.getBalance(
          fromAddress,
          tokenAddress
        );
        const { balanceInUnits: senderTokenBalanceInUnits } =
          senderTokenBalance;

        if (senderTokenBalanceInUnits < Number(amount)) {
          return {
            success: false,
            message: "INSUFFICIENT_BALANCE_FOR_SENDAMOUNT",
          };
        }

        if (!recipientATAExists) {
          ataRent = await this.getATARentExemptionLamports();
        }

        const { balanceInUnits: senderBalanceInUnits } = senderBalance;

        if (
          senderBalanceInUnits < minBalForRentSender ||
          senderBalanceInUnits - ataRent - fee < minBalForRentSender
        ) {
          return {
            success: false,
            message: "INSUFFICIENT_BALANCE_FOR_RENT",
          };
        }
      }

      if (!this.feePayer) {
        const recentBlockhash = await this.connection.getLatestBlockhash();

        const transaction = await this.getSendTxnObject(
          fromAddress,
          recipientAddress,
          amount,
          tokenAddress,
          recentBlockhash
        );
        // transaction.feePayer = senderPubkey;
        const fees = await transaction.getEstimatedFee(this.connection);
        console.log(
          `Estimated ${amount} ${
            tokenAddress ? tokenAddress : "SOL"
          } transfer cost: ${fees} lamports`
        );

        if (fees) {
          const { balanceInUnits } = await this.getBalance(fromAddress);

          const feesInSol = (fees + ataRent) / LAMPORTS_PER_SOL;
          const totalAmountInclFees = tokenAddress
            ? fees + ataRent
            : fees + amountInLamports;

          if (totalAmountInclFees > balanceInUnits) {
            return {
              success: false,
              message: SOLANA_ERROR.INSUFFICIENT_BALANCE,
            };
          }

          return {
            success: true,
            fee: { eth: feesInSol?.toString(), usd: "0" },
          };
        } else {
          return {
            success: false,
            message: "Fees returned null, please try again!",
          };
        }
      } else {
        const feesInSol = (fee + ataRent) / LAMPORTS_PER_SOL;

        return { success: true, fee: { eth: feesInSol?.toString(), usd: "0" } };
      }
    } catch (err: any) {
      console.error(`Error in solana getSendFee: ${err}`);
      return {
        success: false,
        message: customErrorMessage(err) || CHAIN_ERROR.SEND_TOKEN,
      };
    }
  }

  async getSendTxnObject(
    fromAddress: string,
    recipientAddress: string,
    amount: string,
    tokenAddress?: string | null,
    recentBlockhash?: Readonly<{
      blockhash: string;
      lastValidBlockHeight: number;
    }>
  ): Promise<Transaction> {
    try {
      const recipientPubKey = new PublicKey(recipientAddress);
      const senderPubkey = new PublicKey(fromAddress);

      console.log("feePayer: ", this.feePayer || senderPubkey);

      let transaction = new Transaction({
        feePayer: this.feePayer || senderPubkey,
        ...(recentBlockhash
          ? { recentBlockhash: recentBlockhash.blockhash }
          : {}),
      });

      if (tokenAddress) {
        // Get token mint - try standard token program first, then Token Extensions if needed
        let mint;
        let tokenProgramId = TOKEN_PROGRAM_ID;
        const tokenPublicKey = new PublicKey(tokenAddress);
        
        try {
          mint = await getMint(this.connection, tokenPublicKey, undefined, TOKEN_PROGRAM_ID);
          tokenProgramId = TOKEN_PROGRAM_ID;
        } catch (error) {
          if (error instanceof TokenInvalidAccountOwnerError) {
            // Try with Token Extensions Program (Token22)
            mint = await getMint(this.connection, tokenPublicKey, undefined, TOKEN_2022_PROGRAM_ID);
            tokenProgramId = TOKEN_2022_PROGRAM_ID;
          } else {
            throw error;
          }
        }
        
        const [
          recipientTokenAccount,
          fromTokenAccount,
          recipientTokenAccountExists,
        ] = await Promise.all([
          getAssociatedTokenAddress(mint.address, recipientPubKey, true, tokenProgramId),
          getAssociatedTokenAddress(mint.address, senderPubkey, true, tokenProgramId),
          this.checkRecipientATAExists(mint.address, recipientPubKey, tokenProgramId),
        ]);

        if (!recipientTokenAccountExists) {
          // Add ATA creation instruction (payer pays rent)
          transaction.add(
            createAssociatedTokenAccountInstruction(
              senderPubkey, // payer of rent (~0.00204 SOL)
              recipientTokenAccount, // ATA to create
              recipientPubKey, // owner of the ATA
              mint.address, // SPL token mint
              tokenProgramId // Use the correct token program
            )
          );
        }

        // Check if token has transfer hooks and use appropriate instruction
        const transferHook = getTransferHook(mint);
        
        if (transferHook && tokenProgramId === TOKEN_2022_PROGRAM_ID) {
          // Use transfer hook instruction for Token Extensions with hooks
          const transferInstruction = await createTransferCheckedWithTransferHookInstruction(
            this.connection,
            fromTokenAccount,
            mint.address,
            recipientTokenAccount,
            senderPubkey,
            BigInt(amount),
            mint.decimals,
            [],
            undefined, // commitment
            tokenProgramId
          );
          transaction.add(transferInstruction);
        } else {
          // Use regular transfer instruction
          transaction.add(
            createTransferInstruction(
              fromTokenAccount,
              recipientTokenAccount,
              senderPubkey,
              Number(amount), // parse the amount
              [],
              tokenProgramId // Use the correct token program
            )
          );
        }
      } else {
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: senderPubkey,
            toPubkey: recipientPubKey,
            lamports: Number(amount),
          })
        );
      }
      return transaction;
    } catch (err) {
      console.error(`Error in solana getSendTxnObject: ${err}`);
      throw err;
    }
  }

  async createAtaIx({
    mint,
    recipientPubKey,
    payerPubKey,
    tokenProgramId,
  }: any): Promise<CreateAtaIxResponse> {
    try {
      // Use the provided token program ID - it should always be provided now
      const programId = tokenProgramId || TOKEN_PROGRAM_ID;
      
      let recipientTokenAccountPubKey = await getAssociatedTokenAddress(
        mint.address,
        recipientPubKey,
        true,
        programId
      );

      // console.log({ recipientTokenAccountPubKey });

      const accountInfo = await this.connection.getAccountInfo(
        recipientTokenAccountPubKey
      );

      let recipientTokenAccountIx;
      if (!accountInfo) {
        console.log("Recipient token account does not exist, creating...");
        recipientTokenAccountIx = createAssociatedTokenAccountInstruction(
          payerPubKey,
          recipientTokenAccountPubKey,
          recipientPubKey,
          mint.address,
          programId
        );
      }

      return {
        publicKey: recipientTokenAccountPubKey,
        txnIx: recipientTokenAccountIx,
      };
    } catch (err) {
      console.error(`Error in solana createAtaIx: ${err}`);
      throw err;
    }
  }

  private async isNetworkCongested(): Promise<boolean> {
    const samples = await this.connection.getRecentPerformanceSamples(1);
    if (samples && samples.length > 0) {
      const slotTime = samples[0].numTransactions / samples[0].numSlots;
      // Assume network is congested if transactions per slot exceed a threshold (e.g., 5)
      return slotTime > 5;
    }
    return false;
  }

  async signAndSendTransaction({
    privateKey,
    signer,
    transaction,
    sender,
    gas,
  }: {
    privateKey?: string;
    signer?: ILitCustomAuthSignerSolana;
    sender?: Keypair;
    transaction: Transaction;
    gas?: Gas;
  }): Promise<TxnResponse> {
    try {
      const recentBlockhash = await this.connection.getLatestBlockhash();

      let senderPubKey;

      if (!sender && privateKey) {
        sender = this.getPayer(privateKey);
        senderPubKey = sender.publicKey;
      } else if (signer) {
        senderPubKey = new PublicKey(await signer?.getAddress());
      } else {
        return {
          success: false,
          message: "SIGNER_PRIVATE_KEY_UNDEFINED",
        };
      }

      const feePayer = this.feePayer || senderPubKey;
      console.log("feePayer:", feePayer.toBase58());

      // Create a new transaction
      let txn = new Transaction({
        ...(recentBlockhash
          ? { recentBlockhash: recentBlockhash.blockhash }
          : {}),
        feePayer,
      });
      txn.add(transaction);
      txn.feePayer = feePayer;

      // const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      //   units: 1000000,
      // });

      // Get priority fee
      // const priorityFeeMicroLamports = await this.getPrioritizationFees({
      //   publicKey: senderPubKey,
      // });
      // console.log("priorityFeeMicroLamports: ", priorityFeeMicroLamports);

      // const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      //   microLamports: priorityFeeMicroLamports,
      // });
      // txn.add(addPriorityFee);

      if (this.triaFeeDepositAddress && gas && this.isGasAbstracted) {
        console.log(
          `Cutting the platform fee of ${gas.token?.amount} ${gas.token?.name}`
        );
        // && some platform fee percent > 0
        txn = await this.transferToken({
          transaction: txn,
          senderPubKey: senderPubKey,
          recipientPubKey: this.triaFeeDepositAddress,
          payerPubKey: this.feePayer || senderPubKey,
          amount: gas.token?.amount as string, // gasAmount,
          tokenAddress: gas.token?.address as string, // gas token address
        });
      }

      const serializedTransaction = txn
        .serialize({
          requireAllSignatures: false,
          verifySignatures: false,
        })
        .toString("base64");

      let txnSignature;

      // Shyft sign
      if (this.feePayer) {
        console.log(
          "Sponsored transaction: Shyft feePayer",
          this.feePayer.toBase58()
        );
        let signedEncodedTxn: string;
        if (sender) {
          txn.partialSign(sender);
          signedEncodedTxn = txn
            .serialize({ requireAllSignatures: false })
            .toString("base64");
        } else {
          console.log("Solana signer.signTransaction");
          const address = await signer?.getAddress();
          // Sign with signer
          signedEncodedTxn = await (
            signer as ILitCustomAuthSignerSolana
          ).partialSign({
            chain: this.network,
            serializedTransaction,
            address: address as string,
          });
          console.log({ signedEncodedTxn });
        }

        console.log("Signing on shyft: ", this.chainName, signedEncodedTxn);
        const res = (await this.shyftService?.signViaShyft({
          chainName: this.chainName,
          encodedTransaction: signedEncodedTxn,
        })) as ShyftSignResponse;
        console.log("shyft res: ", res);
        if (!res?.success) {
          console.error(`Failed signViaShyft: ${res}`);
          return {
            success: false,
            message: "SHYFT_SIGN_FAILED",
          };
        }
        txnSignature = res.data.txnSignature;
      } else {
        console.log("Not sponsored solana txn");

        if (sender) {
          txnSignature = await this.connection.sendTransaction(txn, [sender]);
        } else {
          // Signer will exist
          console.log("Solana signer.signTransaction and broadcast");

          const signedEncodedTxn = await (
            signer as ILitCustomAuthSignerSolana
          ).signTransaction({
            chain: this.network,
            serializedTransaction,
            address: (await signer?.getAddress()) as string,
          });

          try {
            // Send the signed transaction directly
            const txid = await this.connection.sendRawTransaction(
              Buffer.from(signedEncodedTxn, "base64"),
              {
                skipPreflight: true,
                maxRetries: 3,
              }
            );

            await this.waitForConfirmation(this.connection, txid);
            txnSignature = txid;
          } catch (error) {
            errorHandler(error, "solana_signAndSendTransaction");

            console.error("Failed to send transaction:", error);
            throw new Error(`Transaction failed: ${(error as Error).message}`);
          }
        }
      }

      return this.getTxnResponse(txnSignature);
    } catch (err: any) {
      errorHandler(err, "solana_signAndSendTransaction");

      console.error(`Error in signAndSendTransaction: ${err}`);

      return {
        success: false,
        message: "SIGN_AND_SEND_TXN",
      };
    }
  }

  private async waitForConfirmation(connection: any, signature: string) {
    const maxTries = 20;
    for (let i = 0; i < maxTries; i++) {
      const status = await connection.getSignatureStatus(signature);
      const confirmation = status?.value?.confirmationStatus;
      if (confirmation === "confirmed" || confirmation === "finalized") {
        console.log("Transaction confirmed:", confirmation);
        return;
      }
      await new Promise((res) => setTimeout(res, 1500));
    }
    throw new Error("Transaction confirmation timed out.");
  }

  private async getPrioritizationFees({ publicKey }: { publicKey: PublicKey }) {
    try {
      const addressPublicKey = new PublicKey(publicKey);

      const config = {
        lockedWritableAccounts: [addressPublicKey],
      };

      const prioritizationFeeObjects =
        await this.connection.getRecentPrioritizationFees(config);

      if (prioritizationFeeObjects.length === 0) {
        console.log("No prioritization fee data available.");
        return 50000;
      }

      // Filter out prioritization fees that are equal to 0 for other calculations
      const nonZeroFees = prioritizationFeeObjects
        .map((feeObject) => feeObject.prioritizationFee)
        .filter((fee) => fee !== 0);

      const averageFeeExcludingZeros =
        nonZeroFees.length > 0
          ? Math.floor(
              nonZeroFees.reduce((acc, fee) => acc + fee, 0) /
                nonZeroFees.length
            )
          : 0;

      return averageFeeExcludingZeros * 1.5;
    } catch (error) {
      console.error("Error calculating priority fee:", error);
      return 50_000;
    }
  }

  async estimateSolBalanceChange(transaction: Transaction) {
    try {
      const txn = await this.connection.simulateTransaction(
        transaction,
        undefined,
        true
      );
      return txn.value.unitsConsumed;
    } catch (err) {
      console.error(`Error in estimateSolBalanceChange: ${err}`);
      throw err;
    }
  }

  async getEstimatedFee(transaction: Transaction): Promise<FeeResponse> {
    const fees = await transaction.getEstimatedFee(this.connection);

    if (fees) {
      const feesInSol = fees / LAMPORTS_PER_SOL;
      return { success: true, fee: { eth: feesInSol?.toString(), usd: "0" } };
    } else
      return {
        success: false,
        message: "FEES_NULL",
      };
  }

  getTxnInstructions(transaction: Transaction): TxnInstruction[] {
    /** For Sol balance change */
    // const txn = await this.connection.simulateTransaction(
    //   transaction,
    //   undefined,
    //   true
    // );
    // console.log("Simulate: ", txn.value);
    // console.log("Accounts: ", txn.value.accounts);

    let instructions: TxnInstruction[] = [];

    // console.log("Instructions: ");

    transaction.instructions.forEach((itx, index) => {
      // console.log(`Instruction ${index + 1}`);
      // console.log("Keys:");
      // itx.keys.forEach((key) => {
      //   console.log(
      //     `Public Key: ${key.pubkey.toBase58()}, Signer: ${
      //       key.isSigner
      //     }, Writable: ${key.isWritable}`
      //   );
      // });

      const programId = itx.programId.toBase58();
      const data = itx.data.toString("hex");

      instructions.push({ name: "Unknown", programId, data });
    });
    return instructions;
  }

  async send({
    signer,
    privateKey,
    recipientAddress,
    amount,
    tokenAddress,
    gas,
  }: SendPayload): Promise<TxnResponse> {
    console.log("send:", {
      signer,
      privateKey,
      recipientAddress,
      amount,
      tokenAddress,
      gas,
    });
    try {
      let sender;
      let senderPubKey;
      if (privateKey) {
        sender = this.getPayer(privateKey);
        senderPubKey = sender.publicKey;
      } else if (signer) {
        senderPubKey = new PublicKey(await signer.getAddress());
      } else
        return {
          success: false,
          message: "SIGNER_PRIVATE_KEY_UNDEFINED",
        };

      const payerPubKey = this.feePayer || senderPubKey;

      let transaction;
      // if (!tokenAddress) {
      //   transaction = await this.getSendTxnObject(
      //     fromAddress as string,
      //     recipientAddress,
      //     amount,
      //     tokenAddress as string | null
      //   );
      // } else {
      // Error with getSendTxnObject
      // failed to send transaction: Transaction simulation failed: Error processing Instruction 0: invalid account data for instruction

      // Create a new transaction
      transaction = new Transaction();

      const recipientPubKey = new PublicKey(recipientAddress);

      transaction = await this.transferToken({
        transaction,
        senderPubKey,
        recipientPubKey,
        payerPubKey,
        tokenAddress: tokenAddress as string,
        amount,
      });
      // }

      const res = await this.signAndSendTransaction({
        signer,
        sender,
        transaction,
        gas,
      });
      return res;

      // const tx = await this.connection.getParsedTransaction(txnSignature); // .getTransaction(signature);
    } catch (err: any) {
      console.error(`Error in solana send: ${err}`);
      return {
        success: false,
        message: customErrorMessage(err) || CHAIN_ERROR.SEND_TOKEN,
      };
    }
  }

  async transferToken({
    transaction,
    senderPubKey,
    recipientPubKey,
    payerPubKey,
    amount,
    tokenAddress,
  }: {
    transaction: Transaction;
    senderPubKey: PublicKey;
    recipientPubKey: PublicKey;
    payerPubKey: PublicKey;
    amount: string;
    tokenAddress?: string;
  }): Promise<Transaction> {
    try {
      if (tokenAddress) {
        // Get token mint - try standard token program first, then Token Extensions if needed
        let mint;
        let tokenProgramId = TOKEN_PROGRAM_ID;
        const tokenPublicKey = new PublicKey(tokenAddress);
        
        try {
          mint = await getMint(this.connection, tokenPublicKey, undefined, TOKEN_PROGRAM_ID);
          tokenProgramId = TOKEN_PROGRAM_ID;
        } catch (error) {
          if (error instanceof TokenInvalidAccountOwnerError) {
            // Try with Token Extensions Program (Token22)
            mint = await getMint(this.connection, tokenPublicKey, undefined, TOKEN_2022_PROGRAM_ID);
            tokenProgramId = TOKEN_2022_PROGRAM_ID;
          } else {
            throw error;
          }
        }
        
        // Get the token account of the from address, and if it does not exist, create it
        // const fromTokenAccount = await getOrCreateAssociatedTokenAccount(
        //   this.connection,
        //   sender, // payer
        //   mint.address, // mintx
        //   sender.publicKey // owner
        // );

        // Get the token account of the from address, and if it does not exist, create it
        const fromTokenAccountPubKey = await getAssociatedTokenAddress(
          mint.address,
          senderPubKey,
          true,
          tokenProgramId
        );

        const {
          publicKey: recipientTokenAccountPubKey,
          txnIx: createRecipientTokenAccountTx,
        } = await this.createAtaIx({ mint, recipientPubKey, payerPubKey, tokenProgramId });

        if (createRecipientTokenAccountTx) {
          console.log("createRecipientTokenAccountTx");
          transaction.add(createRecipientTokenAccountTx);
        }

        // Check if token has transfer hooks and use appropriate instruction
        const transferHook = getTransferHook(mint);
        
        let transferTokensIx;
        if (transferHook && tokenProgramId === TOKEN_2022_PROGRAM_ID) {
          // Use transfer hook instruction for Token Extensions with hooks
          transferTokensIx = await createTransferCheckedWithTransferHookInstruction(
            this.connection,
            fromTokenAccountPubKey,
            mint.address,
            recipientTokenAccountPubKey,
            senderPubKey,
            BigInt(amount),
            mint.decimals,
            [],
            undefined, // commitment
            tokenProgramId
          );
        } else {
          // Use regular transfer instruction
          transferTokensIx = createTransferInstruction(
            fromTokenAccountPubKey,
            recipientTokenAccountPubKey,
            senderPubKey,
            parseInt(amount), // parse the amount
            [],
            tokenProgramId // Use the correct token program
          );
        }

        transaction.add(transferTokensIx);
      } else {
        const amountInLamports = amount;

        // Transfer exactly the amount specified by user
        // Validation layer already ensures amount is sufficient to cover rent if needed
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: senderPubKey,
            toPubkey: recipientPubKey,
            lamports: Number(amountInLamports),
          })
        );
      }

      return transaction;
    } catch (err) {
      console.error(`Error in solana transferToken: ${err}`);
      throw err;
    }
  }

  async getSendNftFee(
    fromAddress: string,
    recipientAddress: string,
    tokenAddress: string
  ): Promise<FeeResponse> {
    try {
      // const senderPubkey = new PublicKey(fromAddress);
      const recentBlockhash = await this.connection.getLatestBlockhash();

      const transaction = await this.getSendNftTxnObject(
        fromAddress,
        recipientAddress,
        tokenAddress,
        recentBlockhash
      );

      // transaction.feePayer = senderPubkey;
      const fees = await transaction.getEstimatedFee(this.connection);

      if (fees) {
        const feesInSol = fees / LAMPORTS_PER_SOL;
        return { success: true, fee: { eth: feesInSol?.toString(), usd: "0" } };
      } else {
        return {
          success: false,
          message: "FEES_NULL",
        };
      }
    } catch (err) {
      console.error(`Error in solana getSendNftFee: ${err}`);
      return {
        success: false,
        message: CHAIN_ERROR.SEND_NFT,
      };
    }
  }

  async getSendNftTxnObject(
    fromAddress: string,
    recipientAddress: string,
    tokenAddress: string,
    recentBlockhash?: Readonly<{
      blockhash: string;
      lastValidBlockHeight: number;
    }>
  ): Promise<Transaction> {
    try {
      const senderPubkey = new PublicKey(fromAddress);
      const receiverPubKey = new PublicKey(recipientAddress);
      const mintPubKey = new PublicKey(tokenAddress);

      let transaction = new Transaction({
        ...(recentBlockhash
          ? { recentBlockhash: recentBlockhash.blockhash }
          : {}),
        feePayer: this.feePayer || senderPubkey,
      });

      const senderTokenAccount = await getAssociatedTokenAddress(
        mintPubKey,
        senderPubkey,
        true
      );
      const receiverTokenAccount = await getAssociatedTokenAddress(
        mintPubKey,
        receiverPubKey,
        true
      );

      transaction.add(
        createTransferInstruction(
          senderTokenAccount,
          receiverTokenAccount,
          senderPubkey,
          1, // Amount is 1 because NFTs are unique
          []
        )
      );

      return transaction;
    } catch (err) {
      console.error(`Error in solana getSendNftTxnObject: ${err}`);
      throw err;
    }
  }

  async sendNft({
    signer,
    privateKey,
    recipientAddress,
    tokenAddress,
    gas,
  }: SendNftInput): Promise<TxnResponse> {
    try {
      console.log(`Sending NFT ${tokenAddress} to ${recipientAddress}`);
      // Create a keypair for the sender from the secret key
      let sender, senderPubKey;

      if (privateKey) {
        sender = this.getPayer(privateKey);
        senderPubKey = sender.publicKey;
      } else if (signer) {
        senderPubKey = new PublicKey(await signer.getAddress());
      } else {
        return {
          success: false,
          message: "SIGNER_PRIVATE_KEY_UNDEFINED",
        };
      }

      // Convert receiver's public key string to a PublicKey object
      const receiverPublicKey = new PublicKey(recipientAddress);

      // Convert mint address string to a PublicKey object
      const mintPublicKey = new PublicKey(tokenAddress);

      console.log("Fetching sender token account...");
      // Find the sender's token account for the NFT
      const senderTokenAccount = await getAssociatedTokenAddress(
        mintPublicKey,
        senderPubKey,
        true
      );

      console.log("Fetching receiver token account...");
      // Find the receiver's associated token address
      const receiverTokenAccount = await getAssociatedTokenAddress(
        // this.connection,
        // sender,
        mintPublicKey,
        receiverPublicKey,
        true
        // undefined
        // 'processed'
        // { maxRetries: 5 }
      );

      // const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      //   units: 1000000,
      // });

      // const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      //   microLamports: 10,
      // });

      let transaction = new Transaction();
      // .add(modifyComputeUnits)
      // .add(addPriorityFee);

      const exists = await this.checkIfTokenAccountExists(receiverTokenAccount);

      if (!exists) {
        console.log("Create Receiver token account!");

        transaction.add(
          createAssociatedTokenAccountInstruction(
            senderPubKey,
            receiverTokenAccount,
            receiverPublicKey,
            mintPublicKey
          )
        );
      }

      // Create itx and add a transaction to transfer the NFT
      transaction.add(
        createTransferInstruction(
          senderTokenAccount,
          receiverTokenAccount,
          senderPubKey,
          1 // Amount is 1 because NFTs are unique
        )
      );

      const res = await this.signAndSendTransaction({
        signer,
        sender,
        transaction,
        gas,
      });
      return res;
    } catch (err: any) {
      console.error(`Error in solana sendNft: ${err}`);
      return {
        success: false,
        message: customErrorMessage(err) || CHAIN_ERROR.SEND_NFT,
      };
    }
  }

  getPayer(privateKey: string): Keypair {
    try {
      let secretKey;
      if (privateKey.split(",").length > 1) {
        secretKey = new Uint8Array(privateKey.split(",") as any);
      } else {
        secretKey = decode(privateKey);
      }

      // fetching the public key from the secret key
      const payer = Keypair.fromSecretKey(secretKey, {
        skipValidation: false,
      });

      return payer;
    } catch (err) {
      console.error(`Error in getPayer: ${err}`);
      throw err;
    }
  }

  private getTxnResponse(txnSignature: string): TxnResponse {
    console.log({ txnSignature });
    return {
      success: true,
      data: {
        txnId: txnSignature,
        viewInExplorer: viewInExplorerLink(txnSignature, this.devnet),
        wait: async () => {
          console.log(
            `Waiting for ${viewInExplorerLink(txnSignature, this.devnet)}`
          );
          await this.waitForTransaction(txnSignature);
        },
      },
    };
  }

  // https://solanacookbook.com/references/keypairs-and-wallets.html#how-to-sign-and-verify-messages-with-wallets

  async signMessage({
    signer,
    privateKey,
    message,
  }: {
    signer?: ILitCustomAuthSignerSolana;
    privateKey?: string;
    message: string;
  }): Promise<string | { success: false; message: string }> {
    try {
      if (privateKey) {
        const messageBytes = naclUtil.decodeUTF8(message);
        const signature = nacl.sign.detached(messageBytes, decode(privateKey));
        return Buffer.from(signature).toString("hex");
      } else if (signer) {
        return await signer.signMessage(message);
      } else {
        return {
          success: false,
          message: "SIGNER_PRIVATE_KEY_UNDEFINED",
        };
      }
    } catch (err) {
      console.error(`Error in solana signMessage: ${err}`);
      throw err;
    }
  }

  async verifySignature(
    signature: string,
    message: string,
    address: string
  ): Promise<boolean> {
    try {
      const messageBytes = naclUtil.decodeUTF8(message);
      const publicKeyBytes = decode(address);
      const result = nacl.sign.detached.verify(
        messageBytes,
        Buffer.from(signature, "hex"),
        publicKeyBytes
      );
      return result;
    } catch (err) {
      console.error(`Error in solana verifySignature: ${err}`);
      throw err;
    }
  }

  async waitForTransaction(
    txnId: string,
    confirmations?: number | undefined,
    _timeout?: number | undefined
  ): Promise<boolean> {
    try {
      if (confirmations && confirmations > 10) {
        await this.connection.confirmTransaction(txnId, "finalized");
      } else {
        await this.connection.confirmTransaction(txnId, "confirmed");
      }
      return true;
    } catch (err) {
      console.error(`Error in solana waitForTransaction: ${err}`);
      throw err;
    }
  }

  async checkIfTokenAccountExists(receiverTokenAccount: PublicKey) {
    // Check if the receiver's token account exists
    try {
      await getAccount(
        this.connection,
        receiverTokenAccount,
        "confirmed",
        TOKEN_PROGRAM_ID
      );

      return true;
    } catch (thrownObject) {
      const error = thrownObject as Error;
      // error.message is am empty string
      if (error.name === "TokenAccountNotFoundError") {
        return false;
      }
      console.error(`Error in checkIfTokenAccountExists: ${error}`);
      throw error;
    }
  }

  async signAndSendVersionedTransaction({
    signer,
    transaction,
  }: {
    signer: ILitCustomAuthSignerSolana;
    transaction: VersionedTransaction;
  }): Promise<TxnResponse> {
    try {
      // If feePayer is set, reconstruct the transaction with the new feePayer
      let modifiedTransaction = transaction;
      let useShyftSponsorship = false;

      if (this.feePayer) {
        useShyftSponsorship = true;

        // Check if the transaction already has the correct feePayer
        const currentFeePayer = transaction.message.staticAccountKeys[0]; // First account is the feePayer

        if (!currentFeePayer.equals(this.feePayer)) {
          try {
            // Get recent blockhash
            const recentBlockhash = await this.connection.getLatestBlockhash();

            // Extract instructions from the existing transaction
            const message = transaction.message;

            // Resolve address lookup tables if present
            let lookupTableAccounts = undefined;
            if (
              message.addressTableLookups &&
              message.addressTableLookups.length > 0
            ) {
              lookupTableAccounts = [];

              for (const lookup of message.addressTableLookups) {
                try {
                  const lookupTableAccount =
                    await this.connection.getAddressLookupTable(
                      lookup.accountKey
                    );
                  if (lookupTableAccount.value) {
                    lookupTableAccounts.push(lookupTableAccount.value);
                  }
                } catch (error) {
                  console.error(
                    "Failed to resolve lookup table:",
                    lookup.accountKey.toBase58(),
                    error
                  );
                  throw new Error(
                    `Failed to resolve address lookup table: ${lookup.accountKey.toBase58()}`
                  );
                }
              }
            }

            // Get all account keys (static + lookup table resolved)
            const allAccountKeys = message.getAccountKeys({
              addressLookupTableAccounts: lookupTableAccounts,
            });

            // Use TransactionMessage.decompile to safely extract instructions
            const decompiled = TransactionMessage.decompile(message, {
              addressLookupTableAccounts: lookupTableAccounts || [],
            });

            const instructions = decompiled.instructions;

            // Create new TransactionMessage with the sponsor feePayer
            const newMessage = new TransactionMessage({
              payerKey: this.feePayer,
              recentBlockhash: recentBlockhash.blockhash,
              instructions: instructions,
            });

            // Create new VersionedTransaction with sponsor as feePayer
            modifiedTransaction = new VersionedTransaction(
              newMessage.compileToV0Message(lookupTableAccounts || [])
            );
          } catch (error) {
            console.error(
              "Failed to reconstruct transaction with new feePayer:",
              error
            );
            console.warn(
              "Falling back to original transaction - will rely on Shyft for sponsorship"
            );
            modifiedTransaction = transaction;
          }
        }
      }

      const serialized = modifiedTransaction.serialize();
      const base64Txn = Buffer.from(serialized).toString("base64");

      let txnSignature: string;

      // Shyft sign
      if (useShyftSponsorship && this.feePayer) {
        const address = await signer?.getAddress();
        // Sign with signer
        const signedEncodedTxn = await (
          signer as ILitCustomAuthSignerSolana
        ).partialSign({
          chain: this.network,
          serializedTransaction: base64Txn,
          address: address as string,
        });

        const res = (await this.shyftService?.signViaShyft({
          chainName: this.chainName,
          encodedTransaction: signedEncodedTxn,
        })) as ShyftSignResponse;

        if (!res?.success) {
          console.error(`Failed signViaShyft: ${res}`);
          throw Error(SOLANA_ERROR.SHYFT_SIGN_FAILED);
        }

        txnSignature = res.data.txnSignature;
      } else {
        const signedEncodedTxn = await signer.signTransaction({
          chain: this.network,
          serializedTransaction: base64Txn,
          address: await signer.getAddress(),
        });

        txnSignature = await this.connection.sendRawTransaction(
          Buffer.from(signedEncodedTxn, "base64"),
          {
            skipPreflight: true,
            maxRetries: 3,
          }
        );
      }

      // await this.waitForConfirmation(this.connection, txid);

      return this.getTxnResponse(txnSignature);
    } catch (err: any) {
      console.error(`Error in signAndSendVersionedTransaction: ${err}`);

      return {
        success: false,
        data: {
          txnId: "",
          viewInExplorer: "",
          wait: () => Promise.resolve(),
        },
        message:
          customErrorMessage(err) ||
          "Failed to sign and send versioned transaction",
        error: err,
      };
    }
  }

  async broadCastOnSolana({
    signer,
    transactionString,
  }: {
    signer: ILitCustomAuthSignerSolana;
    transactionString: string;
  }): Promise<TxnResponse> {
    try {
      const transaction = VersionedTransaction.deserialize(
        Buffer.from(transactionString, "base64")
      );

      const response = await this.signAndSendVersionedTransaction({
        signer,
        transaction,
      });

      return response;
    } catch (err: any) {
      console.error(`Error in solana broadCastOnSolana: ${err}`);
      return {
        success: false,
        data: {
          txnId: "",
          viewInExplorer: "",
          wait: () => Promise.resolve(),
        },
        message:
          customErrorMessage(err) || "Error broadcasting transaction on Solana",
        error: err,
      };
    }
  }
  async checkTxnStatusByHash(
    txnHash: string,
    options?: {
      confirmations?: number;
      timeout?: number;
    }
  ): Promise<{
    success: boolean;
    message: string;
  }> {
    const confirmations = options?.confirmations ?? 1;
    const timeout = options?.timeout ?? 30_000;

    const commitment = confirmations > 10 ? "finalized" : "confirmed";

    const startTime = Date.now();

    try {
      while (Date.now() - startTime < timeout) {
        const status = await this.connection.getSignatureStatus(txnHash, {
          searchTransactionHistory: true,
        });

        const value = status?.value;

        if (value) {
          if (value.err) {
            return {
              success: false,

              message: JSON.stringify(value.err),
            };
          }

          if (value.confirmationStatus === commitment) {
            return {
              success: true,
              message: "Txn Successful",
            };
          }
        }

        await new Promise((res) => setTimeout(res, 1500));
      }

      return {
        success: false,

        message: "Transaction confirmation timed out",
      };
    } catch (err: any) {
      console.error("Error in checkTxnStatusByHash:", err);
      return {
        success: false,

        message: err?.message ?? "Unknown error",
      };
    }
  }

  async getMinBalForRent(address: string) {
    const accountInfo = await this.connection.getAccountInfo(
      new PublicKey(address)
    );

    const accountDataSize = accountInfo?.data.length ?? 0;

    const minBalForRentExemption =
      await this.connection.getMinimumBalanceForRentExemption(accountDataSize);

    return minBalForRentExemption;
  }

  async checkRecipientATAExists(mint: PublicKey, recipient: PublicKey, tokenProgramId?: PublicKey) {
    // Determine token program if not provided - default to TOKEN_PROGRAM_ID for backward compatibility
    const programId = tokenProgramId || TOKEN_PROGRAM_ID;
    
    // Compute the recipient's ATA for this token
    const ata = await getAssociatedTokenAddress(
      mint,
      recipient,
      true, // allowOwnerOffCurve = false
      programId
    );

    try {
      // Try fetching the ATA account details
      await getAccount(this.connection, ata, undefined, programId);

      // ✅ Recipient's ATA exists. You can send SPL tokens directly without paying rent.
      return true;
    } catch (e) {
      // Recipient's ATA does NOT exist. Will require ~0.00204 SOL rent for creation.
      return false;
    }
  }

  async getATARentExemptionLamports() {
    // ATA accounts have a fixed size of 165 bytes
    const ATA_ACCOUNT_SIZE = 165;

    // Fetch the current rent exemption value for 165 bytes
    const rentLamports =
      await this.connection.getMinimumBalanceForRentExemption(ATA_ACCOUNT_SIZE);

    return rentLamports;
  }
}
